import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import AddressAutocomplete from '../components/AddressAutocomplete';
import '../styles/Booking.css';

interface MerchItem {
  id: string;
  name: string;
  price: number;
  description: string;
  image: string;
}

const Checkout: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [item, setItem] = useState<MerchItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [ordered, setOrdered] = useState(false);
  
  const EXPEDITED_FEE = 5;

  const getNextSunday = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() + (7 - day) % 7 + (day === 0 ? 7 : 0);
    const nextSunday = new Date(d.setDate(diff));
    return nextSunday.toISOString().split('T')[0];
  };

  const [standardDate] = useState(getNextSunday());
  const [isTooLate] = useState(() => {
    const day = new Date().getDay();
    return day === 0 || day >= 4; // Sunday (0), Thursday (4), Friday (5), Saturday (6)
  });

  // Form State
  const [address, setAddress] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [deliveryOption, setDeliveryOption] = useState<'standard' | 'expedited'>(isTooLate ? 'expedited' : 'standard');
  const [customDeliveryDate, setCustomDeliveryDate] = useState('');
  const [customerName, setCustomerName] = useState(user?.name || '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetch(`${API_URL}/api/merchandise`)
      .then(res => res.json())
      .then(data => {
        const found = data.find((m: MerchItem) => m.id === id);
        setItem(found || null);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching item:', err);
        setLoading(false);
      });
  }, [id, user, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Address Validation for Nova Scotia
    const nsRegex = /(Nova Scotia|NS|B[0-9][A-Z] [0-9][A-Z][0-9])/i;
    if (!nsRegex.test(address)) {
      setError('Invalid address: We currently only deliver within Nova Scotia. Please include "NS" or "Nova Scotia" in your address.');
      return;
    }

    if (!address || !customerName || (deliveryOption === 'expedited' && !customDeliveryDate)) {
      setError('Please fill in all required fields');
      return;
    }

    const finalDeliveryDate = deliveryOption === 'standard' ? standardDate : customDeliveryDate;
    const totalPrice = (item?.price || 0) * quantity + (deliveryOption === 'expedited' ? EXPEDITED_FEE : 0);

    fetch(`${API_URL}/api/notifications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        parentEmail: user?.email,
        itemName: item?.name,
        price: item?.price,
        quantity: quantity,
        totalPrice: totalPrice,
        address: address,
        deliveryDate: finalDeliveryDate,
        deliveryType: deliveryOption,
        customerName: customerName,
        message: `Order: ${quantity}x ${item?.name} - Deliver to ${address} on ${finalDeliveryDate} (${deliveryOption})`,
        type: 'order'
      })
    })
      .then(res => res.json())
      .then(() => {
        setOrdered(true);
        setTimeout(() => navigate('/dashboard'), 3000);
      })
      .catch(err => {
        console.error('Error placing order:', err);
        setError('Failed to place order. Please try again.');
      });
  };

  if (loading) return <div className="booking-container"><div className="loading-state">Loading checkout...</div></div>;
  if (!item) return <div className="booking-container"><h2>Item not found</h2></div>;

  const currentTotalPrice = item.price * quantity + (deliveryOption === 'expedited' ? EXPEDITED_FEE : 0);

  return (
    <div className="booking-container">
      <Link to={`/shopping/${id}`} className="back-link">← Back to Item</Link>
      <div className="booking-card">
        <div className="booking-header">
          <img src={item.image} alt={item.name} className="booking-icon" style={{ borderRadius: '10px' }} />
          <h1>Checkout: {item.name}</h1>
          <p className="price-tag" style={{ fontSize: '1.2rem', color: 'var(--primary-color)', fontWeight: 'bold' }}>
            Total: ${currentTotalPrice}
          </p>
        </div>

        {ordered ? (
          <div className="success-msg">
            <h2>🎉 Order Placed!</h2>
            <p>Your order for {quantity}x {item.name} has been received.</p>
            <p>Delivery scheduled for: <strong>{deliveryOption === 'standard' ? standardDate : customDeliveryDate}</strong></p>
            <p>Payment of <strong>${currentTotalPrice}</strong> will be collected in cash upon delivery.</p>
          </div>
        ) : (
          <form className="booking-form" onSubmit={handleSubmit}>
            <div className="user-info-summary" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f7ff', borderRadius: '8px' }}>
              <p style={{ margin: '0 0 5px 0' }}>Item: <strong>{item.name}</strong></p>
              <p style={{ margin: '0 0 5px 0' }}>Unit Price: <strong>${item.price}</strong></p>
              <p style={{ margin: 0 }}>Shipping: <strong style={{ color: '#4caf50' }}>FREE</strong></p>
            </div>

            {isTooLate && (
              <div style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '12px', borderRadius: '10px', marginBottom: '20px', fontSize: '0.9rem', border: '1px solid #ffeeba' }}>
                <strong>⚠️ Order Cutoff Passed</strong><br/>
                Orders for free Sunday delivery must be placed by Wednesday. A $5 fee applies for choosing your own delivery day.
              </div>
            )}

            {error && <div className="auth-error">{error}</div>}

            <div className="form-group">
              <label>Quantity</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="secondary-btn" style={{ padding: '5px 15px', minWidth: '40px' }}>-</button>
                <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{quantity}</span>
                <button type="button" onClick={() => setQuantity(quantity + 1)} className="secondary-btn" style={{ padding: '5px 15px', minWidth: '40px' }}>+</button>
              </div>
            </div>

            <div className="form-group">
              <label>Your Full Name</label>
              <input 
                type="text" 
                value={customerName} 
                onChange={(e) => setCustomerName(e.target.value)} 
                placeholder="Name for delivery"
                required 
              />
            </div>

            <div className="form-group">
              <label>Delivery Address (Nova Scotia)</label>
              <AddressAutocomplete 
                value={address} 
                onChange={setAddress} 
                placeholder="e.g. 123 Maple St, Apt 4" 
                required 
              />
            </div>

            <div className="form-group">
              <label>Delivery Option</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  fontWeight: 'normal', 
                  cursor: isTooLate ? 'not-allowed' : 'pointer',
                  opacity: isTooLate ? 0.5 : 1
                }}>
                  <input 
                    type="radio" 
                    checked={deliveryOption === 'standard'} 
                    onChange={() => setDeliveryOption('standard')} 
                    disabled={isTooLate}
                  />
                  <div>
                    <strong>Standard Delivery (FREE)</strong>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Sunday, {standardDate}</div>
                  </div>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'normal', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    checked={deliveryOption === 'expedited'} 
                    onChange={() => setDeliveryOption('expedited')} 
                  />
                  <div>
                    <strong>Pick my own day (+${EXPEDITED_FEE})</strong>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Choose any day for faster delivery.</div>
                  </div>
                </label>
              </div>
            </div>

            {deliveryOption === 'expedited' && (
              <div className="form-group">
                <label>Select Delivery Date</label>
                <input 
                  type="date" 
                  value={customDeliveryDate} 
                  onChange={(e) => setCustomDeliveryDate(e.target.value)} 
                  min={new Date().toISOString().split('T')[0]}
                  required 
                />
              </div>
            )}

            <button type="submit" className="book-submit-btn" style={{ marginTop: '20px' }}>
              Place Order - Pay ${currentTotalPrice} on Delivery
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Checkout;
